import { useEffect, useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { MOBILE_REGEX, NotificationAction } from '@masfront/common-b2c'
import { useFormikContext } from 'formik'

import useSelectLine from './useSelectLine'

import { OPERATOR_FAILED, OPERATOR_NOT_FOUND, TRY_AGAIN } from '../constants/errors'
import { UseOperatorDonor } from '../model/line.model'
import { fetchOperatorData, resetOperatorData } from '../store/line.reducer'

import { SignUp } from '~/basket/model/lines/signup.model'
import useModal from '~/modal/hooks/useModal'

export default function useOperatorDonor(
  isPortability: boolean,
  lineNumber: string,
  id: string,
): UseOperatorDonor {
  const dispatch = useDispatch()
  const { operator, isOperatorLoading, operatorError } = useSelectLine(id)
  const { setModalConfig, setIsOpen } = useModal()

  const { setStatus, setFieldError, status, errors, setErrors } = useFormikContext<SignUp>()

  const operatorFailed: boolean = useMemo(
    () => (isOperatorLoading === false ? !!(!operator?.id || operatorError) : false),
    [isOperatorLoading, operator, operatorError],
  )

  const errorText: string = useMemo(() => {
    if (operatorError === '406') return 'Debes realizar la venta como cliente existente'
    if (operatorError) return OPERATOR_FAILED
    if (!operator?.id) return OPERATOR_NOT_FOUND
    return ''
  }, [operator, operatorError])

  const onGetOperatorInformation = useCallback(
    () => dispatch(fetchOperatorData({ id, lineNumber })),
    [id, lineNumber, dispatch],
  )

  const operatorActions: NotificationAction[] = operatorError
    ? [
        {
          button: { variant: 'contained', size: 'medium', color: 'secondary' },
          name: TRY_AGAIN,
          fn: onGetOperatorInformation,
        },
      ]
    : []

  useEffect(() => {
    if (
      isPortability &&
      MOBILE_REGEX.test(lineNumber) &&
      !operator?.id &&
      isOperatorLoading === null
    ) {
      onGetOperatorInformation()
    }
  }, [isPortability, lineNumber, onGetOperatorInformation, operator, isOperatorLoading])

  useEffect(() => {
    if (!isPortability && !operator?.id) {
      dispatch(resetOperatorData(id))
    }
  }, [operator, isPortability])

  useEffect(() => {
    dispatch(resetOperatorData(id))
  }, [lineNumber])

  useEffect(() => {
    setStatus({
      ...status,
      lineNumber: {
        loading: isOperatorLoading,
        error: isOperatorLoading === false ? errorText : '',
      },
    })
    if (errorText && isOperatorLoading === false) {
      setFieldError('lineNumber', errorText)
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { lineNumber, ...error } = errors
      setErrors(error)
    }
  }, [isOperatorLoading, errorText, setStatus, id])

  useEffect(() => {
    if (operatorError !== '406') {
      setModalConfig({
        open: false,
        title: errorText,
        actions: operatorActions,
        type: 'error',
      })
      setIsOpen(!!operatorFailed)
    }
  }, [operatorFailed])

  return {
    isOperatorLoading,
  }
}
